import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { ScrollLockGuard } from "@/lib/bodyScrollLock";
import { LEGACY_FILTER_MAP } from "@/lib/activity";
import { ChatDock } from "@/components/ChatDock";
import { CookieBanner } from "@/components/CookieBanner";
import { LanguageBanner } from "@/components/LanguageBanner";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { NativeBackButton } from "@/components/NativeBackButton";
import { ServiceUpdatingOverlay } from "@/components/ServiceUpdatingOverlay";
import { ReconnectOverlay } from "@/components/ReconnectOverlay";
import { AppBootSkeleton } from "@/components/FeedSkeleton";
import { UploadQueueProvider } from "@/context/UploadQueueContext";
import { useApp } from "@/context/AppContext";
import { DeepLinkHandler } from "@/components/DeepLinkHandler";
import { PositionMemoryGuard } from "@/components/PositionMemoryGuard";
import { ScrollToTop } from "@/components/ScrollToTop";
import { OfflineQueueBridge } from "@/components/OfflineQueueBridge";
import { VolumeKeyBridge } from "@/components/VolumeKeyBridge";
import { guardOn } from "@/lib/guards";
import { OgUrlSync } from "@/components/OgUrlSync";
import { LoginPrompt } from "@/components/LoginPrompt";
import { ShareNotice } from "@/components/ShareNotice";
import { DonationThanksOverlay, DonorThanksOverlay } from "@/components/DonationThanksOverlay";
import { OpenInAppBanner } from "@/components/OpenInAppBanner";
import { UpdateBanner } from "@/components/UpdateBanner";
import { RollbackBanner } from "@/components/RollbackBanner";
import { NowPlayingBar } from "@/components/NowPlayingBar";
import { AppTrackingGate } from "@/components/AppTrackingGate";
import { AgeAssuranceGate } from "@/components/AgeAssuranceGate";
import { IapAutoSync } from "@/components/IapAutoSync";
import { PushPrimerCard } from "@/components/PushPrimerCard";
import { ProfileCompletionGate } from "@/components/ProfileCompletionCard";
import { phoneNav } from "@/lib/platform";
import { getToken } from "@/lib/api";
import { Capacitor } from "@capacitor/core";
import { hideSplashAfterFirstPaint } from "@/lib/splash";
// Eager: the three screens a user can land on first — everything else is
// code-split so the initial bundle (and first paint) stays small on mobile.
import Onboarding from "@/pages/Onboarding";
import Welcome from "@/pages/Welcome";
import { welcomeRequired, nativeConsentFlow } from "@/lib/welcomeConsent";
import Home from "@/pages/Home";
import Reels from "@/pages/Reels";

// Native app (phones AND tablets): CSS offsets for the always-on bottom nav (see index.css).
if (phoneNav()) document.documentElement.classList.add("nav-phone");

const Saved = lazy(() => import("@/pages/Saved"));
const AdsManager = lazy(() => import("@/pages/AdsManager"));
const AdCredits = lazy(() => import("@/pages/AdCredits"));
const Matching = lazy(() => import("@/pages/Matching"));
const Churches = lazy(() => import("@/pages/Churches"));
const ChurchDetail = lazy(() => import("@/pages/ChurchDetail"));
const ChurchReplays = lazy(() => import("@/pages/ChurchReplays"));
const Groups = lazy(() => import("@/pages/Groups"));
const GroupDetail = lazy(() => import("@/pages/GroupDetail"));
const Events = lazy(() => import("@/pages/Events"));
const EventInvite = lazy(() => import("@/pages/EventInvite"));
const Prayers = lazy(() => import("@/pages/Prayers"));
const Testimonies = lazy(() => import("@/pages/Testimonies"));
const ReadingPlans = lazy(() => import("@/pages/ReadingPlans"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const Live = lazy(() => import("@/pages/Live"));
const LiveWaitingRoom = lazy(() => import("@/pages/LiveWaitingRoom"));
const ScheduledLivesPage = lazy(() => import("@/pages/ScheduledLivesPage"));
const LiveNowPage = lazy(() => import("@/pages/LiveNowPage"));
const Replay = lazy(() => import("@/pages/Replay"));
const Profile = lazy(() => import("@/pages/Profile"));
const ProfilePeople = lazy(() => import("@/pages/ProfilePeople"));
const Support = lazy(() => import("@/pages/Support"));
const Settings = lazy(() => import("@/pages/Settings"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const CollectionView = lazy(() => import("@/pages/CollectionView"));
const Messages = lazy(() => import("@/pages/Messages"));
const Admin = lazy(() => import("@/pages/Admin"));
const Legal = lazy(() => import("@/pages/Legal"));
const ChildSafety = lazy(() => import("@/pages/ChildSafety"));
const AccountDeletion = lazy(() => import("@/pages/AccountDeletion"));
const AccountRecovery = lazy(() => import("@/pages/AccountRecovery"));
const PostComments = lazy(() => import("@/pages/PostComments"));
const Discover = lazy(() => import("@/pages/Discover"));
const InviteFriends = lazy(() => import("@/pages/InviteFriends"));
const MyLivesPage = lazy(() => import("@/pages/MyLivesPage"));
const SearchPage = lazy(() => import("@/pages/Search"));
const HashtagPage = lazy(() => import("@/pages/Hashtag"));
const GetApp = lazy(() => import("@/pages/GetApp"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Faq = lazy(() => import("@/pages/Faq"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Resources = lazy(() => import("@/pages/Resources"));
const ResourceArticle = lazy(() => import("@/pages/ResourceArticle"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const ShareResolver = lazy(() => import("@/pages/ShareResolver"));

const RequireAuth = ({ children }) => {
  const { user, loading } = useApp();
  if (loading) return <AppBootSkeleton />;
  if (!user) {
    // Shared link opened while signed out: remember the destination so the member lands on
    // it right after signing in / registering (DeepLinkHandler consumes the stash).
    try {
      const dest = window.location.pathname + window.location.search;
      if (dest && dest !== "/" && dest !== "/home") {
        window.__yalolaPendingDeepLink = dest;
        sessionStorage.setItem("yalola.pendingDeepLink", dest);
      }
    } catch { /* noop */ }
    return <Navigate to="/?tab=login" replace />;
  }
  return children;
};

// « / » : legacy story share links (yalola.app/?story=<id>) go through the resolver ; a signed-in
// member lands on the feed ; a WEB visitor sees the public « Découvrir » feed (SEO / AdSense : real
// content instead of a login wall) unless he explicitly asked to sign in (?tab=login|register) ;
// the native app keeps the sign-in / sign-up screen, precedee au 1er lancement de l'ecran « Bienvenue »
// (CGU + Politique acceptees une fois par appareil, cf. lib/welcomeConsent).
const RootRoute = ({ user, config }) => {
  const [params] = useSearchParams();
  const [needWelcome, setNeedWelcome] = React.useState(welcomeRequired);
  const story = params.get("story");
  const sid = params.get("sid");
  // Lot 602 — ancien lien admin / notification « /?story=<groupe>&sid=<story> » : c'est un lien de GROUPE,
  // pas un lien de partage (où ?story=<id de story>) → on le route vers la rangée du Fil.
  if (story && sid) return <Navigate to={`/home?story=${encodeURIComponent(story)}&sid=${encodeURIComponent(sid)}`} replace />;
  if (story) return <Navigate to={`/s/story/${encodeURIComponent(story)}`} replace />;
  if (user) return <Navigate to="/home" replace />;
  // Lot 705 — lien d'invitation (?ref=CODE) ouvert par un visiteur : droit à l'inscription, code conservé dans l'URL
  // (et déjà mémorisé par lib/referral.js) — avant, il tombait sur « Découvrir » et le code était perdu.
  const ref = params.get("ref");
  if (ref && !params.get("tab")) return <Navigate to={`/?tab=register&ref=${encodeURIComponent(ref)}`} replace />;
  if (needWelcome) return <Welcome onAccept={() => setNeedWelcome(false)} />;
  const discoverOn = config?.settings?.features?.public_discover !== false;
  if (!params.get("tab") && !nativeConsentFlow() && discoverOn) return <Discover />;
  return <Onboarding />;
};

const DashboardRedirect = () => {
  const { user } = useApp();
  return <Navigate to={user ? `/profile/${user.id}` : "/"} replace />;
};

// Legacy « Transactions » page (app 1.5.2 links, bookmarks) → Profil › Mon espace › Paiements & finances
// (avec un filtre hérité → directement la liste « Mes transactions » filtrée).
const HistoryRedirect = () => {
  const { user } = useApp();
  const [params] = useSearchParams();
  const tx = LEGACY_FILTER_MAP[params.get("filter")] || "";
  return <Navigate to={`/profile/${user.id}?tab=espace${tx ? `&tx=${tx}#mes-transactions` : "#paiements-finances"}`} replace />;
};

// Présentation statique d'index.html (#yalola-static, hors #root) : sur le WEB, un visiteur sans
// session la garde comme écran de chargement (vrai texte pour les robots qui capturent la page
// pendant le boot) ; membres et app native passent directement au squelette.
export const ADMOB_BOOT_DELAY_MS = 3000;
const removeStaticIntro = () => document.getElementById("yalola-static")?.remove();
// Lot 702 — natif : l'écran d'accueil disparaît à la première image peinte par React (squelette ou app), pas à 600 ms.
const keepStaticIntroWhileLoading = () => !getToken() && !Capacitor.isNativePlatform();

function App() {
  const { user, loading, config } = useApp();

  React.useLayoutEffect(() => {
    if (!keepStaticIntroWhileLoading()) removeStaticIntro();
    hideSplashAfterFirstPaint();
  }, []);
  React.useLayoutEffect(() => {
    if (!loading) removeStaticIntro();
  }, [loading]);

  React.useEffect(() => {
    const s = document.getElementById("yalola-splash");
    if (s) { s.classList.add("hide"); setTimeout(() => s.remove(), 500); }
    // Wipe any residual live-replay cache files on app start so nothing accumulates
    // on the device across lives (native only, best-effort).
    import("@/lib/liveReplay").then((m) => m.cleanupReplayCache()).catch(() => {});
    // Native: route share links (yalola:// + https://yalola.app) into the app + pull
    // the real Android system-bar insets so the UI never sits under the nav buttons.
    import("@/lib/deepLinks").then((m) => m.initDeepLinks()).catch(() => {});
    import("@/lib/nativeInsets").then((m) => m.initNativeInsets()).catch(() => {});
    import("@/lib/iosKeyboard").then((m) => m.initIosKeyboard()).catch(() => {});
    import("@/lib/androidKeyboard").then((m) => m.initAndroidKeyboard()).catch(() => {});
    import("@/lib/externalLinks").then((m) => m.initNativeExternalLinks()).catch(() => {});
    // Écran maintenu allumé pendant un direct et la lecture d'une vidéo avec le son (lot 594).
    import("@/lib/screenAwake").then((m) => m.installVideoScreenAwake()).catch(() => {});
    // Vrai plein écran vidéo : paysage forcé à l'entrée (vidéos horizontales / YouTube), retour à l'orientation d'avant.
    import("@/lib/videoFullscreen").then((m) => m.installFullscreenOrientation()).catch(() => {});
    // Idle warm-up: quietly fetch EVERY main route chunk (spaced out). Once a
    // chunk is imported, navigating to it no longer needs the network — pages
    // keep opening even while a big video upload saturates the connection
    // (user report: URL changed but the page never loaded during an import).
    const warm = setTimeout(() => {
      if (!guardOn("route_warmup")) return; // lot 671 — garde-fou admin « Préchargement des écrans »
      [
        () => import("@/pages/Profile"),
        () => import("@/pages/Messages"),
        () => import("@/pages/AccountDeletion"),
        () => import("@/pages/Legal"),
        () => import("@/pages/Notifications"),
        () => import("@/pages/PostComments"),
        () => import("@/pages/Groups"),
        () => import("@/pages/Events"),
        () => import("@/pages/Marketplace"),
        () => import("@/pages/Live"),
        () => import("@/pages/MyLivesPage"),
        () => import("@/pages/LiveNowPage"),
        () => import("@/pages/Churches"),
        () => import("@/pages/ChurchDetail"),
        () => import("@/pages/Discover"),
        () => import("@/pages/Settings"),
        () => import("@/pages/Saved"),
        () => import("@/pages/Prayers"),
        () => import("@/pages/GroupDetail"),
        () => import("@/pages/AdsManager"),
        () => import("@/pages/Admin"),
      ].forEach((f, i) => setTimeout(() => f().catch(() => {}), i * 400));
    }, 3500);
    return () => clearTimeout(warm);
  }, []);

  // Native only: initialize Google AdMob (+ UMP consent) once the admin config arrives — SIGNED-IN members only.
  // Fresh install: nothing on Welcome / Sign in (iOS: ATT already answered on Welcome) → after login, on the feed:
  // ATT (idempotent) → UMP → initialize, before any ad. Existing signed-in installs: at boot, as before.
  // Guests never trigger UMP (AdmobNative slots also wait for a member).
  // Lot 702 — démarrage étalé : le SDK AdMob (+ consentement UMP) s'initialise 3 s après le premier rendu, pas pendant
  // le démarrage (initialisation lourde sur le fil principal Android = saccades / risque d'ANR au lancement).
  React.useEffect(() => {
    if (!(user && config?.settings?.admob)) return undefined;
    const t = setTimeout(() => { import("@/lib/admob").then((m) => m.initAdMob(config.settings)).catch(() => {}); }, ADMOB_BOOT_DELAY_MS);
    return () => clearTimeout(t);
  }, [config, user]);

  // Carte « Activer les notifications ? » : réglages admin (interrupteurs / délai / textes) lus par lib/nativePush.
  React.useEffect(() => {
    if (config?.settings) import("@/lib/nativePush").then((m) => m.setPushPrimerSettings(config.settings)).catch(() => {});
  }, [config]);

  if (loading) return keepStaticIntroWhileLoading() ? null : <AppBootSkeleton />;

  return (
    <div className="App">
      <ServiceUpdatingOverlay />
      <ReconnectOverlay />
      <BrowserRouter>
        <UploadQueueProvider>
          <MaintenanceGate>
            <NativeBackButton />
            <DeepLinkHandler />
            <PositionMemoryGuard />
            <ScrollToTop />
            <OfflineQueueBridge />
            <VolumeKeyBridge />
            <ScrollLockGuard />
            <OgUrlSync />
            <ShareNotice />
            <DonationThanksOverlay />
            <DonorThanksOverlay />
            <OpenInAppBanner />
            <UpdateBanner />
            <RollbackBanner />
            <NowPlayingBar />
            <LoginPrompt />
            <AppTrackingGate />
            <AgeAssuranceGate />
            <IapAutoSync />
            <PushPrimerCard />
            <ProfileCompletionGate />
            <Suspense fallback={<AppBootSkeleton />}>
            <Routes>
            <Route path="/" element={<RootRoute user={user} config={config} />} />
            <Route path="/s/:kind/:id" element={<ShareResolver />} />
            <Route path="/decouvrir" element={<Discover />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/reels" element={<Reels />} />
            <Route path="/saved" element={<RequireAuth><Saved /></RequireAuth>} />
            <Route path="/ads" element={<RequireAuth><AdsManager /></RequireAuth>} />
            <Route path="/ads/credits" element={<RequireAuth><AdCredits /></RequireAuth>} />
            <Route path="/matching" element={<RequireAuth><Matching /></RequireAuth>} />
            <Route path="/churches" element={<Churches />} />
            <Route path="/churches/:id" element={<ChurchDetail />} />
            <Route path="/churches/:id/replays" element={<ChurchReplays />} />
            <Route path="/groups" element={<RequireAuth><Groups /></RequireAuth>} />
            <Route path="/groups/:id" element={<RequireAuth><GroupDetail /></RequireAuth>} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventInvite />} />
            <Route path="/prayers" element={<Prayers />} />
            <Route path="/testimonies" element={<RequireAuth><Testimonies /></RequireAuth>} />
            <Route path="/reading" element={<RequireAuth><ReadingPlans /></RequireAuth>} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/live" element={<Live />} />
            <Route path="/lives" element={<RequireAuth><LiveNowPage /></RequireAuth>} />
            <Route path="/lives/scheduled" element={<RequireAuth><ScheduledLivesPage /></RequireAuth>} />
            <Route path="/live/waiting/:id" element={<RequireAuth><LiveWaitingRoom /></RequireAuth>} />
            <Route path="/replay/:id" element={<RequireAuth><Replay /></RequireAuth>} />
            <Route path="/profile/:id" element={<Profile />} />
            <Route path="/profile/:id/followers" element={<ProfilePeople tab="followers" />} />
            <Route path="/profile/:id/following" element={<ProfilePeople tab="following" />} />
            <Route path="/profile/:id/suggestions" element={<ProfilePeople tab="suggestions" />} />
            <Route path="/support" element={<RequireAuth><Support /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
            <Route path="/history" element={<RequireAuth><HistoryRedirect /></RequireAuth>} />
            <Route path="/collections/:id" element={<RequireAuth><CollectionView /></RequireAuth>} />
            <Route path="/messages" element={<RequireAuth><Messages /></RequireAuth>} />
            <Route path="/invite" element={<RequireAuth><InviteFriends /></RequireAuth>} />
            <Route path="/mes-directs" element={<RequireAuth><MyLivesPage /></RequireAuth>} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/hashtag/:tag" element={<HashtagPage />} />
            <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
            <Route path="/admin/:tab" element={<RequireAuth><Admin /></RequireAuth>} />
            {/* Shared post: readable signed out (read-only + sign-in invitation), see PostComments. */}
            <Route path="/post/:id" element={<PostComments />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/get-app" element={<GetApp />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/ressources" element={<Resources />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/ressources/:slug" element={<ResourceArticle />} />
            <Route path="/resources/:slug" element={<ResourceArticle />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/legal/child-safety" element={<ChildSafety />} />
            <Route path="/legal/:type" element={<Legal />} />
            <Route path="/supprimer-mon-compte" element={<AccountDeletion />} />
            <Route path="/delete-account" element={<AccountDeletion />} />
            <Route path="/recuperer-mon-compte" element={<AccountRecovery />} />
            <Route path="/recover-account" element={<AccountRecovery />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <ChatDock />
          <CookieBanner />
          <LanguageBanner />
          </MaintenanceGate>
        </UploadQueueProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
